import { Avatar } from 'module/ui/components/atoms/avatar';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import {
  Body01,
  Body02,
  BodyShort01,
  ProductiveHeading01,
} from 'module/ui/ozone/atoms';
import {
  faWarning,
  faLightbulbOn,
  faWrench,
} from '@fortawesome/pro-solid-svg-icons';
import { Tag } from '@rocketlane/ui';
import { Tooltip } from 'antd';
import { CitationType } from 'api/citation/types';
import { SignalCitation, SignalType } from 'api/signal/types';
import { ReportedBy, SignalField } from 'api/timeline/types';
import moment from 'moment';
import React, { useState } from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import styled, { css, keyframes } from 'styled-components';
import { preprocessMessageWithCitations } from 'utils/citation-preprocessor';
import { CitationDataPopover } from 'components/organisms/document-editor/extensions/citation/ui/citation-data-popover';
import {
  PersonInfo,
  PersonMeta,
  StyledPeopleList,
  PeopleNames,
} from './styles';
import {
  formatUserDisplayName,
  formatRoleDisplay,
  isUserUnknown,
} from './utils';
import {
  MeetingTranscriptDrawer,
  MeetingTranscriptDrawerState,
} from '../meeting-transcript/drawer';

interface TimelineSignalCardProps {
  title: string;
  description: string;
  people: ReportedBy[] | ReportedBy;
  reason?: SignalField;
  signalType: SignalType;
  signalName: string;
  createdAt?: Date;
  className?: string;
  citations?: SignalCitation;
  isFirst?: boolean;
  isMeetingsAndEmails?: boolean;
  projectId?: number;
  signalOccurrenceId?: number;
  isHighlighted?: boolean;
}

const HIGHLIGHT_FADE_MS = 3000;

const highlightFadeOut = keyframes`
  from {
    background-color: var(--blue-20);
  }
  to {
    background-color: transparent;
  }
`;

const CardContainer = styled.div<{
  $isFirst?: boolean;
  $isMeetingsAndEmails?: boolean;
  $isHighlighted?: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-03);
  padding: var(--spacing-04);
  border-radius: 12px;

  &:last-child {
    padding-bottom: 0;
    border-bottom: none;
  }

  ${({ $isMeetingsAndEmails }) =>
    $isMeetingsAndEmails &&
    css`
      &:last-child {
        padding-bottom: var(--spacing-07);
        border-bottom: 1px solid var(--gray-10);
      }
    `}

  &[data-highlighted='true'] {
    animation: ${highlightFadeOut} ${HIGHLIGHT_FADE_MS}ms ease-in-out forwards;
  }
`;

const TagsContainer = styled.div`
  display: flex;
  gap: var(--spacing-02);
  flex-wrap: wrap;
  align-items: center;
`;

const ReasonTag = styled(Tag)<{ $signalType: SignalType }>`
  && {
    background-color: ${({ $signalType }) => {
      switch ($signalType) {
        case SignalType.Risk:
          return 'var(--red-10)';
        case SignalType.Opportunity:
          return 'var(--green-10)';
        case SignalType.Operational:
          return 'var(--gray-10)';
        default:
          return 'var(--red-10)';
      }
    }};
    color: ${({ $signalType }) => {
      switch ($signalType) {
        case SignalType.Risk:
          return 'var(--red-70)';
        case SignalType.Opportunity:
          return 'var(--green-70)';
        case SignalType.Operational:
          return 'var(--gray-70)';
        default:
          return 'var(--red-70)';
      }
    }};
    border: none;
    font-weight: 500;
  }
`;

const SignalTypeTagWithIcon = styled(Tag)<{ $signalType: SignalType }>`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-02);
  height: 24px;
  padding: 0 var(--spacing-02);
  border-radius: 4px;
  border: 1px solid var(--gray-20);
  background: var(--white);
  color: var(--text-primary);
  font-size: var(--font-size-sm);
  font-weight: 500;
  line-height: 20px;
`;

const SignalTypeIcon = styled(FontAwesomeIcon)<{ $signalType: SignalType }>`
  font-size: var(--font-size-xs);
  width: 12px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ $signalType }) => {
    switch ($signalType) {
      case SignalType.Risk:
        return 'var(--red-60)';
      case SignalType.Opportunity:
        return 'var(--green-60)';
      case SignalType.Operational:
        return 'var(--icon-secondary)';
      default:
        return 'var(--red-60)';
    }
  }};
`;

const Title = styled(ProductiveHeading01)`
  margin: 0;
  color: var(--text-primary);
  font-weight: 600;
  line-height: var(--line-height-03);
`;

const Description = styled(Body01)`
  color: var(--text-primary);
  line-height: var(--line-height-03);
  font-weight: 400;
  font-size: var(--font-size-base);

  p {
    margin-bottom: 0px;
  }
`;

const StyledCitationPopover = styled(CitationDataPopover)`
  display: inline-block;
`;

const TagWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-02);
`;

const ReporterInfo = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
`;

const Divider = styled.span`
  width: 1px;
  height: 16px;
  background-color: var(--gray-20);
  flex-shrink: 0;
`;

const ReporterTime = styled(Body02)`
  color: var(--text-tertiary);
`;

const PeopleNamesRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-02);
  flex-wrap: wrap;
`;

const AvatarNamePair = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-02);
`;

const formatTimeAgo = (date?: Date): string => {
  if (!date) {
    return '';
  }

  const now = moment();
  const then = moment(date);
  const diffInDays = now.diff(then, 'days');
  const diffInWeeks = now.diff(then, 'weeks');
  const diffInMonths = now.diff(then, 'months');

  if (diffInDays === 0) {
    return 'Today';
  }

  if (diffInDays === 1) {
    return '1 day ago';
  }

  if (diffInDays < 7) {
    return `${diffInDays} days ago`;
  }

  if (diffInWeeks === 1) {
    return '1 week ago';
  }

  if (diffInWeeks < 4) {
    return `${diffInWeeks} weeks ago`;
  }

  if (diffInMonths === 1) {
    return '1 month ago';
  }

  return `${diffInMonths} months ago`;
};

const getSignalTypeIcon = (signalType: SignalType) => {
  switch (signalType) {
    case SignalType.Risk:
      return faWarning;
    case SignalType.Opportunity:
      return faLightbulbOn;
    case SignalType.Operational:
      return faWrench;
    default:
      return faWarning;
  }
};

export const TimelineSignalCard: React.FC<TimelineSignalCardProps> = ({
  title,
  description,
  people,
  reason,
  signalType,
  signalName,
  createdAt,
  className,
  citations,
  isFirst = false,
  isMeetingsAndEmails = false,
  projectId,
  signalOccurrenceId,
  isHighlighted = false,
}) => {
  const [transcriptDrawerState, setTranscriptDrawerState] =
    useState<MeetingTranscriptDrawerState>(null);
  const person = Array.isArray(people) ? people[0] : people;
  const reasonLabel =
    reason?.metaData?.options?.find(
      (option) => option.value === reason?.fieldValue,
    )?.label || '';

  const shouldShowSignalTypeTag =
    signalType === SignalType.Risk ||
    signalType === SignalType.Operational ||
    signalType === SignalType.Opportunity;

  const createMarkdownComponents = (): Partial<Components> => ({
    cite: (props: any) => {
      const citationNum = props['data-num'];

      return (
        <StyledCitationPopover
          label={citationNum}
          citationId={citations?.[citationNum]?.citationUuid ?? 0}
          citationType={citations?.[citationNum]?.citationType as CitationType}
          onViewCitationSource={(params) => {
            if (params.citationType === CitationType.Meeting) {
              setTranscriptDrawerState({
                meetingId: params.citation.metaData.source.meetingId,
                source: params.citation.metaData.source,
                timeToSeek: parseInt(params.currentBlock?.fromTime ?? '0'),
              });
            }
          }}
        />
      );
    },
  });

  const markdownComponents = createMarkdownComponents();

  return (
    <CardContainer
      className={className}
      $isFirst={isFirst}
      $isMeetingsAndEmails={isMeetingsAndEmails}
      $isHighlighted={isHighlighted}
      data-highlighted={isHighlighted ? 'true' : undefined}
      data-signal-occurrence-id={signalOccurrenceId}
    >
      <TagsContainer>
        {shouldShowSignalTypeTag && (
          <SignalTypeTagWithIcon $signalType={signalType} variant="basic">
            <TagWrapper>
              <SignalTypeIcon
                icon={getSignalTypeIcon(signalType)}
                $signalType={signalType}
                size="sm"
              />
              {signalName}
            </TagWrapper>
          </SignalTypeTagWithIcon>
        )}
        {reasonLabel && (
          <ReasonTag variant="basic" $signalType={signalType}>
            {reasonLabel}
          </ReasonTag>
        )}
      </TagsContainer>

      <Title>{title}</Title>

      <Description>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[[rehypeRaw]]}
          components={markdownComponents}
        >
          {preprocessMessageWithCitations(description)}
        </ReactMarkdown>
      </Description>

      {Array.isArray(people) && people.length > 1 ? (
        <PeopleList people={people} createdAt={createdAt} />
      ) : (
        <PersonInfo>
          <Avatar
            size="small"
            name={formatUserDisplayName(person)}
            isUserUnknown={isUserUnknown(person)}
          />
          <ReporterInfo>
            <PersonMeta>
              <BodyShort01>{formatUserDisplayName(person)}</BodyShort01>
              <Body02>{formatRoleDisplay(person)}</Body02>
            </PersonMeta>
            {createdAt && (
              <>
                <Divider />
                <ReporterTime>{formatTimeAgo(createdAt)}</ReporterTime>
              </>
            )}
          </ReporterInfo>
        </PersonInfo>
      )}
      <MeetingTranscriptDrawer
        drawerState={transcriptDrawerState}
        onClose={() => setTranscriptDrawerState(null)}
        projectId={projectId}
      />
    </CardContainer>
  );
};

interface PeopleListProps {
  people: ReportedBy[];
  createdAt?: Date;
}

const PeopleList: React.FC<PeopleListProps> = ({ people, createdAt }) => {
  const [firstPerson, secondPerson, ...rest] = people;
  const remainingCount = rest.length;
  const allNames = people.map((p) => formatUserDisplayName(p)).join(', ');

  return (
    <StyledPeopleList>
      <ReporterInfo>
        <Tooltip title={allNames}>
          <PeopleNamesRow>
            <AvatarNamePair>
              <Avatar
                size="small"
                name={`${firstPerson.firstName} ${firstPerson.lastName}`}
                isUserUnknown={isUserUnknown(firstPerson)}
              />
              <PeopleNames>
                <PersonMeta>{formatUserDisplayName(firstPerson)},</PersonMeta>
              </PeopleNames>
            </AvatarNamePair>
            <AvatarNamePair>
              <Avatar
                size="small"
                name={`${secondPerson.firstName} ${secondPerson.lastName}`}
                isUserUnknown={isUserUnknown(secondPerson)}
              />
              <PeopleNames>
                <PersonMeta>{formatUserDisplayName(secondPerson)}</PersonMeta>
              </PeopleNames>
            </AvatarNamePair>
            {remainingCount > 0 && (
              <PeopleNames>
                <PersonMeta>, +{remainingCount} others</PersonMeta>
              </PeopleNames>
            )}
          </PeopleNamesRow>
        </Tooltip>
        {createdAt && (
          <>
            <Divider />
            <ReporterTime>{formatTimeAgo(createdAt)}</ReporterTime>
          </>
        )}
      </ReporterInfo>
    </StyledPeopleList>
  );
};
